import { useRAF } from '@monogrid/vue-boost/core'
import { useElementSize } from '@vueuse/core'
import { Mesh, OGLRenderingContext, Program, Renderer, RenderTarget, Texture, Triangle, Vec2 } from 'ogl'
import { MaybeRefOrGetter, ref, toValue, watch } from 'vue'

import { getAssetURL } from '@/utils'
import fragment from '@/webgl/shaders/filter/plane.fs.glsl?raw'
import vertex from '@/webgl/shaders/filter/plane.vs.glsl?raw'
import fragFXOutput from '@/webgl/shaders/fx-output/plane.fs.glsl?raw'
import vertFXOutput from '@/webgl/shaders/fx-output/plane.vs.glsl?raw'
import fragmentTarget from '@/webgl/shaders/render-target/plane.fs.glsl?raw'
import vertexTarget from '@/webgl/shaders/render-target/plane.vs.glsl?raw'

import { useStoreSnapshot } from './useStoreSnapshot'

export const useOGL = () => {
  const _container = ref<HTMLElement | null>()
  const _content = ref<HTMLVideoElement | HTMLCanvasElement>()
  const _filter = ref<HTMLVideoElement | HTMLCanvasElement>()
  const _mask = ref<HTMLVideoElement | HTMLCanvasElement>()
  const _background = ref<HTMLVideoElement>()
  const _fx1 = ref<HTMLVideoElement>()
  const _fx2 = ref<HTMLVideoElement>()

  const glContext = ref<OGLRenderingContext>(null)
  const segmentProgram = ref<Program>(null)
  const fxOutput = ref<Program>(null)
  const mainProgram = ref<Program>(null)

  const targetA = ref<RenderTarget>(null)
  const targetB = ref<RenderTarget>(null)

  const { time } = useRAF(null, true)
  const { width: ctnWidth, height: ctnHeight } = useElementSize(_container)
  const { width: contentWidth, height: contentHeight } = useElementSize(_content)
  // const { width: fxBubblesWidth, height: fxBubblesHeight } = useElementSize(_fx1)
  // const { width: fxLeafWidth, height: fxLeafHeight } = useElementSize(_fx2)

  const { updateSnapshotData } = useStoreSnapshot()

  const init = (
    container: HTMLElement,
    filter: MaybeRefOrGetter<HTMLCanvasElement>,
    segmentation: MaybeRefOrGetter<HTMLCanvasElement>,
    segmentationMask: MaybeRefOrGetter<HTMLCanvasElement>,
    videoBackground: HTMLVideoElement,
    fx1: HTMLVideoElement,
    fx2: HTMLVideoElement
  ) => {
    _filter.value = toValue(filter) // Main canvas to display
    _content.value = toValue(segmentation) // RGB Channel segmentation
    _mask.value = toValue(segmentationMask) // Alpha channel for blur segmentation

    _background.value = toValue(videoBackground)
    _fx1.value = toValue(fx1) // Bubbles
    _fx2.value = toValue(fx2) // Leaves
    _container.value = toValue(container)

    const renderer = new Renderer({
      preserveDrawingBuffer: true,
      canvas: _filter.value,
      powerPreference: 'high-performance',
      alpha: true,
      dpr: Math.min(window.devicePixelRatio, 2)
    })

    glContext.value = renderer.gl

    const canvas = glContext.value.canvas

    _container.value.appendChild(canvas)

    glContext.value.clearColor(0, 0, 0, 1)

    targetA.value = new RenderTarget(glContext.value)
    targetB.value = new RenderTarget(glContext.value)

    const geometry = new Triangle(glContext.value)
    const texture = new Texture(glContext.value, { generateMipmaps: false })
    const textureMask = new Texture(glContext.value, { generateMipmaps: false })

    const tNoiseBubbles = new Texture(glContext.value, { generateMipmaps: false })
    const tNoiseLeaves = new Texture(glContext.value, { generateMipmaps: false })

    const textureBackground = new Texture(glContext.value, { generateMipmaps: false })

    // Programs

    segmentProgram.value = new Program(glContext.value, {
      vertex,
      fragment,
      uniforms: {
        tDiffuse: { value: texture },
        tAlpha: { value: textureMask },
        tBackground: { value: textureBackground },
        uResolution: { value: new Vec2() },
        uImageRes: { value: new Vec2() },
        uBgRes: { value: new Vec2(_background.value.videoWidth, _background.value.videoHeight) }
      }
    })

    fxOutput.value = new Program(glContext.value, {
      vertex: vertFXOutput,
      fragment: fragFXOutput,
      uniforms: {
        uResolution: { value: new Vec2() },
        tBubbles: { value: tNoiseBubbles },
        tLeaf: { value: tNoiseLeaves },
        uBubbleResolution: { value: new Vec2(_fx1.value.videoWidth, _fx1.value.videoHeight) },
        uLeafResolution: { value: new Vec2(_fx2.value.videoWidth, _fx2.value.videoHeight) },
        // Controls whether or not the bubbles / leaves should display
        uBubble: { value: 0.0 },
        uLeaf: { value: 0.0 }
      },
      transparent: true
    })

    mainProgram.value = new Program(glContext.value, {
      vertex: vertexTarget,
      fragment: fragmentTarget,
      uniforms: {
        uTime: { value: 0 },
        uResolution: { value: new Vec2() },
        tBaseProgram: { value: targetA.value.texture },
        tFXOutput: { value: targetB.value.texture },
        tSnapshotFinal: { value: null },
        uImageRes: { value: new Vec2() },
        uBubble: { value: 0.0 },
        uLeaf: { value: 0.0 }
      }
    })

    const mesh = new Mesh(glContext.value, { geometry, program: segmentProgram.value })
    const fxOutputMesh = new Mesh(glContext.value, { geometry, program: fxOutput.value })
    const finalMesh = new Mesh(glContext.value, { geometry, program: mainProgram.value })

    const update = () => {
      if (!segmentProgram.value || !_content.value || !_mask.value || !_filter.value || !_background.value) return

      if (_content.value instanceof HTMLCanvasElement && segmentProgram.value.uniforms.tDiffuse.value !== null) {
        // @ts-expect-error OGL types suck
        texture.image = _content.value
        segmentProgram.value.uniforms.tDiffuse.value.needsUpdate = true
      }

      if (_mask.value instanceof HTMLCanvasElement) {
        // @ts-expect-error OGL types suck
        textureMask.image = _mask.value
        segmentProgram.value.uniforms.tAlpha.value.needsUpdate = true
      }

      if (_background.value instanceof HTMLVideoElement && _background.value.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) {
        textureBackground.image = _background.value
        segmentProgram.value.uniforms.tBackground.value.needsUpdate = true
        segmentProgram.value.uniforms.uBgRes.value.set(_background.value.width, _background.value.height)
      }

      if (segmentProgram.value && targetA.value) {
        renderer.render({ scene: mesh, target: targetA.value })
      }

      if (fxOutput.value && targetB.value) {
        if (_fx1.value instanceof HTMLVideoElement && _fx1.value.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) {
          tNoiseBubbles.image = _fx1.value
          fxOutput.value.uniforms.tBubbles.value.needsUpdate = true
          fxOutput?.value?.uniforms?.uBubbleResolution.value.set(_fx1.value.width, _fx1.value.height)
        }

        if (_fx2.value instanceof HTMLVideoElement && _fx2.value.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) {
          tNoiseLeaves.image = _fx2.value
          fxOutput.value.uniforms.tLeaf.value.needsUpdate = true
          fxOutput.value.uniforms?.uLeafResolution.value.set(_fx2.value.width * 0.5, _fx2.value.height)
        }
        renderer.render({ scene: fxOutputMesh, target: targetB.value })
      }

      if (mainProgram.value && targetA.value && targetB.value) {
        // Use textures from render targets as inputs to your final render program
        mainProgram.value.uniforms.uTime.value += 0.05
        mainProgram.value.uniforms.tBaseProgram.value = targetA.value.texture
        mainProgram.value.uniforms.tFXOutput.value = targetB.value.texture

        // Render the final scene
        renderer.render({ scene: finalMesh })
      }
    }

    watch([segmentProgram, ctnWidth, ctnHeight], ([p, w, h]) => {
      p?.uniforms.uResolution.value.set(w, h)
      fxOutput.value.uniforms.uResolution.value.set(w, h)
      mainProgram.value.uniforms.uResolution.value.set(w, h)

      targetA.value.setSize(w, h)
      targetB.value.setSize(w, h)
      renderer.setSize(w, h)
    }, { immediate: true })

    watch([segmentProgram, contentWidth, contentHeight], ([p, w, h]) => {
      p?.uniforms.uImageRes.value.set(w, h)
      mainProgram.value.uniforms?.uImageRes.value.set(w, h)
    }, { immediate: true })

    watch(time, () => {
      update()
    })
  }

  const updateImageResolution = (width: number, height: number) => {
    segmentProgram.value.uniforms?.uImageRes.value.set(width, height)
    mainProgram.value.uniforms?.uImageRes.value.set(width, height)
  }

  const snapFinalContent = () => {
    const tempCanvas = document.createElement('canvas')
    const tempCtx = tempCanvas.getContext('2d')

    tempCanvas.width = _filter.value.width
    tempCanvas.height = _filter.value.height

    const watermark = new Image()
    watermark.src = getAssetURL('textures/watermark.png')
    const x = (tempCanvas.width / 2) - 50
    const y = 70

    watermark.onload = () => {
      tempCtx.drawImage(_filter.value, 0, 0)
      tempCtx.drawImage(watermark, x, y)

      const url = tempCanvas.toDataURL('image/png')

      if (url !== undefined) {
        updateSnapshotData(url)
      }
    }
  }

  return {
    init,
    snapFinalContent,
    updateImageResolution,
    segmentProgram,
    mainProgram,
    fxOutput
  }
}
